import type {
  CollectionReference,
  DocumentData,
  DocumentReference,
  DocumentSnapshot,
  Firestore,
  FirestoreDataConverter,
  OrderByDirection,
  PartialWithFieldValue,
  Query,
  QueryConstraint,
  QueryDocumentSnapshot,
  QuerySnapshot,
  SnapshotOptions,
  Unsubscribe,
  WhereFilterOp,
  WithFieldValue,
} from 'firebase/firestore'
import type { Ref } from 'vue'
import logger from '@/utils/logger'
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  increment,
  limit,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  startAfter,
  updateDoc,
  where,
} from 'firebase/firestore'
import { firestoreDefaultConverter } from 'vuefire'

export interface QueryParam {
  field: string
  operation: WhereFilterOp
  value: any
}

export interface SortParam {
  field: string
  order: OrderByDirection
}

function createGenericConverter<T extends DocumentData>() {
  return {
    toFirestore: firestoreDefaultConverter.toFirestore,
    fromFirestore(
      snapshot: QueryDocumentSnapshot<T>,
      options: SnapshotOptions,
    ) {
      const data = firestoreDefaultConverter.fromFirestore(snapshot, options)
      if (!data)
        return null
      // add anything custom to the returned object
      data.metadata = snapshot.metadata
      return data
    },
  }
}

export class ServiceFactory<T extends DocumentData> {
  private db: Firestore
  protected paths: string
  protected collectionRef: CollectionReference<T, DocumentData>
  protected converter: FirestoreDataConverter<T, DocumentData | { id: string }>

  constructor(db: Firestore, paths: string, converter?: FirestoreDataConverter<T, DocumentData | { id: string }>) {
    this.db = db
    this.paths = paths
    this.converter = converter || createGenericConverter<T>()
    this.collectionRef = collection(this.db, paths).withConverter(this.converter)
  }

  get collectionReference(): CollectionReference<T, DocumentData | { id: string }> {
    return this.collectionRef.withConverter(this.converter)
  }

  async create(payload: WithFieldValue<T>): Promise<string | undefined> {
    try {
      const docRef = await addDoc(this.collectionRef, payload)
      return docRef.id
    } catch (e: unknown) {
      logger(e)
    }
  }

  async set(id: string, payload: PartialWithFieldValue<T>, merge = false): Promise<string | undefined> {
    try {
      await setDoc(doc(this.db, this.paths, id), payload, { merge })
      return id
    } catch (e) {
      logger(e)
    }
  }

  getDocRef(id: string): DocumentReference<T> {
    return doc(this.db, this.paths, id).withConverter(this.converter)
  }

  getDocSnapshot(id: string): Promise<DocumentSnapshot<T>> {
    const docRef = this.getDocRef(id)
    return getDoc(docRef)
  }

  async get(id: string): Promise<T | null> {
    let result: T | null = null
    const docRef = doc(this.db, this.paths, id).withConverter(this.converter)
    const docSnap = await getDoc(docRef)
    if (docSnap.exists())
      result = docSnap.data()

    return result
  }

  async getAll(): Promise<T[]> {
    const querySnapshot = await getDocs(this.collectionRef)
    return querySnapshot.docs.map(doc => doc.data())
  }

  async observe(id: string, refObject: Ref<T>): Promise<Unsubscribe> {
    const docRef = doc(this.db, this.paths, id).withConverter(this.converter)
    const unsubscribeDocumentUpdate = onSnapshot(
      docRef,
      (docSnapshot: any) => {
        if (docSnapshot.exists())
          refObject.value = { id: docSnapshot.id, ...(docSnapshot.data() as T) }
      },
      (err: Error) => {
        logger(err)
      },
    )

    return unsubscribeDocumentUpdate
  }

  async update(id: string, payload: DocumentData = {}): Promise<void> {
    try {
      const docRef = doc(this.db, `${this.paths}/${id}`)
      await updateDoc(docRef, payload)
    } catch (error) {
      console.error(error)
    }
  }

  async delete(id: string): Promise<void> {
    const docRef = doc(this.db, `${this.paths}/${id}`)
    await deleteDoc(docRef)
  }

  querySnapshot(
    conditionParams: QueryParam[],
    orderParams: SortParam[],
    count = 100,
    lastSnapshot?: DocumentSnapshot<T> | null,
  ): Query<DocumentData> {
    const collectionRef = this.collectionRef.withConverter(this.converter)
    conditionParams = conditionParams || []
    orderParams = orderParams || []

    const queryParams: [CollectionReference<T, DocumentData>, ...QueryConstraint[]]
      = [
        collectionRef,
        ...conditionParams.map(v =>
          where(v.field, v.operation, v.value),
        ),
        ...orderParams.map(v => orderBy(v.field, v.order)),
      ]

    if (lastSnapshot)
      queryParams.push(startAfter(lastSnapshot))

    if (count > 0)
      queryParams.push(limit(count))

    return query(...queryParams).withConverter(this.converter)
  }

  async query(
    conditionParams: QueryParam[],
    orderParams: SortParam[],
    count = 100,
  ): Promise<T[]> {
    const q = this.querySnapshot(conditionParams, orderParams, count)
    try {
      const querySnapshot = await getDocs(q) as QuerySnapshot<T>
      const result = getResult(querySnapshot)
      return result
    } catch (error) {
      logger(error)
      return []
    }
  }

  async paginate(
    prevSnapshot: DocumentSnapshot<T> | undefined,
    conditionParams: QueryParam[],
    orderParams: SortParam[],
    count = 100,
  ): Promise<{ result: T[], lastSnapshot: DocumentSnapshot<T> | null }> {
    const q = this.querySnapshot(conditionParams, orderParams, count, prevSnapshot)

    try {
      const res = await getDocs(q)
      const lastSnapshot = res.docs[res.docs.length - 1]

      return {
        result: getResult(res),
        lastSnapshot,
      }
    } catch (error) {
      logger(error)

      return {
        result: [],
        lastSnapshot: null,
      }
    }
  }

  incrementField(id: string, fieldName: string, count = 1): void {
    const plus = increment(count)
    this.update(id, {
      [fieldName]: plus,
    })
  }

  getResource(): string {
    return this.paths
  }
}

function getResult<T>(querySnapshot: QuerySnapshot<T>): T[] {
  const result: T[] = []
  querySnapshot.forEach((doc) => {
    result.push({ id: doc.id, ...(doc.data() as T) })
  })

  return result
}

export default <T extends DocumentData>(paths: string, converter?: FirestoreDataConverter<T, DocumentData>): ServiceFactory<T> => {
  const db = useFirestore()

  return new ServiceFactory<T>(db, paths, converter)
}
